import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { ContextDock } from '../features/agent/ContextDock';
import { PrivacyConsent } from '../features/account/PrivacyConsent';
import { FocusProvider } from '../features/focus/focus-context';
import { LearningPreviewModal } from '../features/learning/LearningPreviewModal';
import { api, ApiError, Viewer } from '../lib/api';
import { DockPayload, ShellContext } from './shell-context';

interface AppShellProps {
  children: ReactNode;
  route: string;
}

const navItems = [
  { id: 'home', label: '首页', href: '/', icon: 'home' },
  { id: 'calendar', label: '日历', href: '/calendar', icon: 'calendar' },
  { id: 'learn', label: '学习', href: '/wordess/', icon: 'book' },
  { id: 'whaleu', label: '信库', href: '/whaleu', icon: 'notice' },
  { id: 'growth', label: '成长', href: '/account#academic-profile', icon: 'data' },
];

function activeFor(route: string, id: string) {
  if (id === 'home') return route === '/';
  if (id === 'whaleu') return route === '/whaleu';
  if (id === 'growth') return route === '/account';
  return route.includes(id);
}

export function AppShell({ children, route }: AppShellProps) {
  const calendarMode = route === '/calendar';
  const focusMode = route === '/focus';
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [dockOpen, setDockOpen] = useState(route === '/agent');
  const [dockPayload, setDockPayload] = useState<DockPayload>({ kind: 'agent' });
  const [learningOpen, setLearningOpen] = useState(false);

  const updateViewer = useCallback((nextViewer: Viewer | null) => {
    setViewer(nextViewer);
    setSessionResolved(true);
  }, []);

  useEffect(() => {
    api<{ data: { viewer: Viewer } }>('/api/v1/session')
      .then((response) => updateViewer(response.data.viewer))
      .catch((error) => { if (error instanceof ApiError && error.status === 401) updateViewer(null); else setSessionResolved(true); });
  }, [updateViewer]);

  const value = useMemo(() => ({
    viewer,
    setViewer: updateViewer,
    openDock(payload: DockPayload = { kind: 'agent' }) { setDockPayload(payload); setDockOpen(true); },
    closeDock() { setDockOpen(false); },
  }), [updateViewer, viewer]);

  const name = viewer?.displayName || (sessionResolved ? '尚未登录' : '正在确认');

  return <FocusProvider><ShellContext.Provider value={value}>
    <div className={`app-shell${calendarMode ? ' calendar-shell' : ''}${focusMode ? ' focus-shell' : ''}`}>
      <aside className="global-rail" aria-label="全局导航">
        <a className="brand" href="/" aria-label="菜根首页">
          <img src="/assets/brand/caigen-root-mark.svg?v=2" alt="" />
          <span><b>菜根</b><small>CAIGEN</small></span>
        </a>
        <nav className="primary-nav">
          {navItems.map((item) => item.id === 'learn'
            ? <button key={item.id} type="button" aria-haspopup="dialog" aria-expanded={learningOpen} onClick={() => setLearningOpen(true)}><Icon name={item.icon} /><span>{item.label}</span></button>
            : <a key={item.id} className={activeFor(route, item.id) ? 'active' : ''} href={item.href}><Icon name={item.icon} /><span>{item.label}</span></a>)}
        </nav>
        <div className="product-nav">
          <p>我的产品</p>
          <a href="/calendar">菜根日历</a>
          <button type="button" aria-haspopup="dialog" onClick={() => setLearningOpen(true)}>Wordess</button>
          <a href="/whaleu">WhaleU</a>
          <a href="/account#academic-profile">成长画像</a>
          <a href="/?view=focus">菜根钟</a>
          <a href="/agent">Agent</a>
        </div>
        <footer className="rail-footer">
          {viewer?.isAdmin ? <a className={route === '/admin' ? 'active' : ''} href="/admin"><Icon name="data" /><span>管理台</span></a> : null}
          <a className={route === '/account' ? 'active' : ''} href={viewer ? '/account' : '/login'}><Icon name="settings" /><span>设置</span></a>
          <a className={`profile${sessionResolved ? '' : ' loading'}`} href={viewer ? '/account' : '/login'} aria-label={viewer ? '打开账户设置' : sessionResolved ? '登录菜根' : '正在确认登录状态'}><span>{sessionResolved ? viewer ? name.slice(0, 1) : '访' : '·'}</span><p>{name}<small>{viewer ? [viewer.profile.school, viewer.profile.grade].filter(Boolean).join(' · ') || '菜根账户' : sessionResolved ? '点击登录菜根' : '读取账户状态'}</small></p></a>
        </footer>
      </aside>
      <main className="primary-viewport" id="main-content">{children}</main>
      {route !== '/agent' && !focusMode ? <button className="assistant-launch" type="button" aria-label="打开菜根助手" onClick={() => value.openDock({ kind: 'agent' })}><Icon name="chat" /><span><b>问问菜根</b><small>AI 助手</small></span></button> : null}
      <ContextDock open={dockOpen && route !== '/agent'} payload={dockPayload} onClose={value.closeDock} />
      <LearningPreviewModal open={learningOpen} onClose={() => setLearningOpen(false)} />
      {viewer && route !== '/account' ? <PrivacyConsent mode="gate" /> : null}
    </div>
  </ShellContext.Provider></FocusProvider>;
}
